import React, { ComponentType } from "react"
import { compose } from "@reduxjs/toolkit"
import { useTranslation } from "react-i18next"
import {
  Box,
  Typography,
  styled,
  AccordionSummaryProps,
  AccordionSummary,
} from "@mui/material"

import {
  AccordionComponent as Accordion,
  AccordionSummaryTitle,
  Details,
} from "components/ui/Accordion"
import { withProducts } from "hocs/features"
import Product from "components/Products/Item"
import { Pay, Actions } from "components/Products/elements"
import { ProductListType, ProductType } from "types"
import { formatMoney } from "helpers/formatters"
import { openDialog } from "redux/features/dialog/dialogSlice"
import { PRODUCT_PAYMENT_MODAL } from "config"
import { useAppDispatch } from "redux/hooks"

type Props = {
  products: ProductListType
  total: number
}

const List = ({ products, total }: Props) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const isEmpty = total === 0

  const handlePaymentCreate = (id: number): void => {
    dispatch(
      openDialog({
        type: PRODUCT_PAYMENT_MODAL,
        content: {
          productId: id,
        },
      }),
    )
  }

  return (
    <Container data-testid="products-list">
      {isEmpty ? (
        <Typography component="span" variant="subtitle1">
          {t("pages.services.empty.title")}
        </Typography>
      ) : (
        <>
          {products.map((item: ProductType) => (
            <Accordion key={item.id}>
              <ActionSummary
                actions={
                  <Actions>
                    <Pay
                      onClick={(event) => {
                        event.stopPropagation()
                        handlePaymentCreate(item.id)
                      }}
                    >
                      {t("features.product.actions.payment.create.title", {
                        amount: formatMoney(item.price),
                      })}
                    </Pay>
                  </Actions>
                }
              >
                {t(`features.product.type.${item.product_type}.title`)}
              </ActionSummary>
              <Details>
                <Product item={item} />
              </Details>
            </Accordion>
          ))}
        </>
      )}
    </Container>
  )
}

export default compose<ComponentType>(withProducts)(List)

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "nowrap",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  margin: "42px 0 8px 0",
  [theme.breakpoints.down("lg")]: {
    flexWrap: "wrap",
    flexDirection: "column",
  },
}))

const IconArrowDown = styled("svg")(({ theme }) => ({
  width: "17px",
  height: "10px",
  fill: "none",
  "& path": {
    stroke: theme.palette.primary.main,
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
  },
}))

const ActionSummary = styled(
  (props: AccordionSummaryProps & { actions: React.ReactNode }) => (
    <AccordionSummary
      expandIcon={
        <IconArrowDown xmlns="http://www.w3.org/2000/svg">
          <path d="m1 1 7.468 7.503L15.94 1" />
        </IconArrowDown>
      }
      {...props}
    >
      <AccordionSummaryTitle>{props.children}</AccordionSummaryTitle>
      {props.actions}
    </AccordionSummary>
  ),
)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  padding: 0,
  minHeight: "auto",
  "& .MuiAccordionSummary-content": {
    margin: 0,
  },
}))
